import {
  Dispatch, SetStateAction, useEffect, useMemo, useState, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TbFlagShare } from 'react-icons/tb';
import { ConnectedProps, connect } from 'react-redux';
import {
  Button,
  Card,
  Column,
  Empty,
  Flex,
  Heading,
  Icon,
  List,
  Row,
  Spacer,
  Spinner,
} from '@oliasoft-open-source/react-ui-library';
import { getTokenPayload } from '@oliasoft/pkce';
import { ActionMap, ability } from '@oliasoft/authorization-ui';
import { AccessControlModal, IAccessControlModalsRef } from '@oliasoft/hierarchy-gui';

import { caslEnums } from 'common/enums/casl-enums';
import { IUser } from 'common/interfaces/customer-interfaces';
import { HierarchyLevelType, IObject } from 'common/interfaces/hierarchy-entities';
import { AccessSubjectTypes } from 'common/interfaces/access-rights';
import ClientConfig from 'client/config';
import translations from 'client/internationalization/translation-map.json';
import { downloadObject } from 'client/components/data-viewer/utils/export';
import { FileUpload } from 'client/components/file-upload/file-upload';
import { ShareObjectModal } from 'client/components/object/share-object-modal';
import { getUsers } from 'client/store/entities/user/user';
import { getApiKeys } from 'client/store/entities/api-key/api-key';
import { getObjectsOfNode, objectsReset } from 'client/store/entities/object/object';
import { ClassificationLevelControlModal } from 'client/components/classification-level-control-modal/classification-level-control-modal';
import { RootState } from 'client/store/configureStore';
import { getEntityAccessRightsList } from 'client/store/entities/access-rights/access-rights';
import { DataExplorerFileDrop } from 'client/views/explore/data-explorer/data-explorer-file-drop';
import { HierarchyTreeItems, IHierarchyTreeItem } from 'client/views/explore/hierarchy/hierarchy.interfaces';
import { dataObjectToTreeItem } from 'client/views/explore/hierarchy/hierarchy.utils';
import { setAccessRightsBatch } from 'client/components/access-control-modal/access-utils';
import { getObjectName } from 'client/views/explore/hierarchy/object-parts';

type DataExplorerProps = PropsFromRedux & {
  hierarchy: HierarchyTreeItems,
  selectedData: IObject | null;
  setSelectedData: Dispatch<SetStateAction<IObject | null>>;
  selectedArchiveId: string | null;
  setDataViewerVisible: (bool: boolean) => void;
  deleteItem: (item: IHierarchyTreeItem) => void;
  getNodeChildrenCount: (parentId: string) => number | undefined;
};

const DataExplorer = ({
  selectedData,
  setSelectedData,
  setDataViewerVisible,
  getObjectsOfNode,
  hierarchy,
  activeItem,
  objects,
  objectsLoading,
  nodesLoading,
  deleteItem,
  getNodeChildrenCount,
  users,
  getUsers,
  selectedCustomer,
  selectedArchiveId,
  entityAccessRightsList,
  entityAccessRightsMetadata,
  getEntityAccessRightsList,
}: DataExplorerProps) => {
  const { t } = useTranslation();
  const childRef = useRef<IAccessControlModalsRef>(null);
  const companyName = selectedCustomer?.companyName;

  const tokenPayload = getTokenPayload(
    ClientConfig.config.pkce.provider,
    ClientConfig.config.applicationId,
    ClientConfig.config.idpServerUrl,
  );

  const [isOpenUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [isOpenShareModal, setOpenShareModal] = useState<boolean>(false);
  const [nodeId, setNodeId] = useState<string>();
  const [files, setFiles] = useState<File[] | undefined>();
  const [isOpenClassificationLevelControlModal, setOpenClassificationLevelControlModal] = useState(false);
  const [classificationLevelHierarchyItem, setClassificationLevelHierarchyItem] = useState<IHierarchyTreeItem | undefined>();

  const closeShareModal = () => setOpenShareModal(false);
  const openAccessControlModal = (item: IHierarchyTreeItem, subjectType = AccessSubjectTypes.User) => {
    childRef.current?.accessRightsConfigurationStarted({
      item,
      userList: users,
      currentUserSubjectId: tokenPayload!.subjectId || '',
      subjectType,
    });
    getEntityAccessRightsList(item.entityType, item.id, subjectType);
  };

  const onCloseClassificationLevelControlModal = () => setOpenClassificationLevelControlModal(false);
  const onOpenClassificationLevelControlModal = (item: IHierarchyTreeItem) => {
    setClassificationLevelHierarchyItem(item);
    setOpenClassificationLevelControlModal(true);
  };

  const hierarchyActiveItem: IHierarchyTreeItem | null = useMemo(
    () => {
      const item = activeItem ? hierarchy[activeItem.id] : null;
      return item
        ? {
          ...item,
          key: item.id,
          parent: item.parentId || 0,
        }
        : null;
    },
    [activeItem, hierarchy],
  );

  const canMangeAccess = ability.can(ActionMap.MANAGE, caslEnums.manageAccessRights);

  const viewData = (data: IObject) => {
    setSelectedData(data);
    setDataViewerVisible(true);
  };

  const resetNodesObjects = () => {
    if (!activeItem) return;
    objectsReset();
  };

  useEffect(() => {
    childRef.current?.accessRightListUpdated({ accessRightsList: entityAccessRightsList, metadata: entityAccessRightsMetadata });
  }, [entityAccessRightsList]);

  useEffect(() => {
    resetNodesObjects();
  }, [selectedCustomer]);

  useEffect(() => {
    if (selectedCustomer?.companyName) {
      getUsers();
    }
  }, [selectedCustomer]);

  const isLoading = useMemo(() => Object.values(nodesLoading).includes(true) || Object.values(objectsLoading).includes(true), [nodesLoading, objectsLoading]);

  const openUploadModal = (nodeid: string) => {
    setNodeId(nodeid);
    setOpenUploadModal(true);
  };

  const closeUploadModal = () => {
    setOpenUploadModal(false);
    setNodeId(undefined);
    setFiles(undefined);
  };

  const onUploadComplete = (nodeid: string | undefined) => {
    if (nodeid) {
      getObjectsOfNode(nodeid);
    }
    setNodeId(undefined);
    setFiles(undefined);
  };

  const onDrop = (files: File[], nodeid: string) => {
    setFiles(files);
    openUploadModal(nodeid || hierarchyActiveItem!.id);
  };

  const getAccessManagementAction = (item: IHierarchyTreeItem, disabled = false) => {
    return (canMangeAccess) ? {
      type: 'Option',
      label: t(translations.authorization_accessControl),
      icon: 'key',
      disabled,
      onClick: () => openAccessControlModal(item),
    } : null;
  };

  const getClassificationLevelManagementAction = (item: IHierarchyTreeItem, disabled = false) => {
    const canMangeClassificationLevel = ability.can(ActionMap.MANAGE, caslEnums.nodeSecurityClassification);
    return (canMangeClassificationLevel) ? {
      type: 'Option',
      label: t(translations.classificationLevel),
      icon: 'key',
      disabled,
      onClick: () => {
        onOpenClassificationLevelControlModal(item);
      },
    } : null;
  };

  const getChildHierarchyItems = () => {
    if (!activeItem?.id) return [];
    const childItems = objects[activeItem.id] || [];

    const items = childItems.map((object) => {
      const owner = users?.find((user: IUser) => user.subjectId === object.owner);
      const treeItem = ({
        id: object.objectid,
        name: (
          <Flex alignItems="center" gap="var(--padding-xs)">
            <Icon icon="file" size={15} />
            {getObjectName(object, null, companyName!, owner!)}
          </Flex>
        ),
        onClick: () => {
          setSelectedData(object);
          if (!object.restricted) {
            viewData(object);
          }
        },
        active: selectedData?.objectid === object.objectid,
        actions: [] as any[],
        restricted: object.restricted,
      });

      const donwloadAction = {
        type: 'Option', icon: 'download', label: t(translations.importExport_download), onClick: () => { downloadObject(object); },
      };

      const shareAction = {
        type: 'Option',
        icon: <TbFlagShare />,
        label: t(translations.share),
        onClick: () => {
          setSelectedData(object);
          setOpenShareModal(true);
        },
      };

      const deleteAction = [{
        type: 'Option', icon: 'delete', label: t(translations.delete), onClick: () => deleteItem(dataObjectToTreeItem(object)),
      }];

      const accessManagementAction = getAccessManagementAction(dataObjectToTreeItem(object));
      const classificationLevelManagementAction = getClassificationLevelManagementAction(dataObjectToTreeItem(object));

      treeItem.actions = [{
        subActions: [
          donwloadAction,
          shareAction,
          ...deleteAction,
          ...(canMangeAccess ? [accessManagementAction] : []),
          ...(classificationLevelManagementAction ? [classificationLevelManagementAction] : []),
        ],
      }];

      if (treeItem.restricted) {
        treeItem.actions[0].subActions = treeItem.actions[0].subActions.map((subAction: any[]) => ({ ...subAction, disabled: true }));
      }

      return treeItem;
    });

    return items;
  };

  const childHierarchyItems = getChildHierarchyItems();
  const hierarchyItemChildList = activeItem && (
    <div>
      <Spacer />
      {childHierarchyItems.length > 0 ? (
        <Card padding={false}>
          <List
            noHeader
            list={{
              items: childHierarchyItems,
            }}
          />
        </Card>
      ) : <Empty height="100%" width="100%" text={t(translations.viewer_noData)} />}
    </div>
  );

  const objectCount = activeItem ? getNodeChildrenCount(activeItem.id) : 0;

  const dataList = (
    <>
      <Row spacing={0}>
        <Column>
          <Heading>
            {activeItem && (`${t(translations.files)} (${objectCount})`)}
            <Spacer width="var(--padding-xxs)" />
            {isLoading && <Flex justifyContent="center"><Spinner dark tiny /></Flex>}
          </Heading>
        </Column>

        {activeItem?.entityType === HierarchyLevelType.Node && (
        <>
          <Spacer width="var(--padding)" />
          <Button
            colored
            label={t(translations.importExport_uploadFile)}
            onClick={() => openUploadModal(hierarchyActiveItem!.id)}
            small
          />

        </>
        )}
      </Row>
      <Spacer />
      <Row>
        <Column>
          {hierarchyItemChildList}
          <Spacer />
        </Column>
      </Row>
    </>
  );

  return (
    <>
      <Column>
        {activeItem && dataList}
      </Column>
      {activeItem
      && activeItem?.entityType !== HierarchyLevelType.Archive
      && (
        <DataExplorerFileDrop onDrop={onDrop} itemName={hierarchyActiveItem?.name || ''} />
      )}
      {nodeId && isOpenUploadModal
        && (
        <FileUpload
          isModal
          visible={isOpenUploadModal}
          onClose={closeUploadModal}
          initialNodeId={nodeId || activeItem?.id}
          onUploadComplete={() => onUploadComplete(nodeId)}
          archiveid={selectedArchiveId!}
          initialFiles={files}
        />
        )}
      {isOpenShareModal && (
      <ShareObjectModal
        objectid={selectedData!.objectid}
        name={selectedData!.name}
        visible={isOpenShareModal}
        onClose={closeShareModal}
      />
      )}
      <AccessControlModal
        ref={childRef}
        handleEntityAccessRightsUpdated={setAccessRightsBatch}
      />
      {
        isOpenClassificationLevelControlModal ? (
          <ClassificationLevelControlModal
            onCloseClassificationLevelControlModal={onCloseClassificationLevelControlModal}
            showModal={isOpenClassificationLevelControlModal}
            selectedItem={classificationLevelHierarchyItem}
          />
        ) : null
      }
    </>
  );
};

const mapStateToProps = ({ entities, ui }: RootState) => ({
  objects: entities?.object.objects,
  objectsLoading: entities?.object.isLoading,
  activeItem: ui.hierarchyTree.activeItem,
  users: entities?.user.users,
  nodes: entities?.nodeTree.items,
  nodesPages: entities?.nodeTree.pages,
  nodesLoading: entities?.nodeTree.isLoading,
  entityAccessRightsList: entities?.entityAccessRights.accessRights,
  entityAccessRightsMetadata: entities?.entityAccessRights.metadata,
  selectedCustomer: entities?.customer.selectedCustomer,
  selectedArchiveId: ui.hierarchyTree.selectedArchiveId,
});

const mapDispatchToProps = {
  getObjectsOfNode,
  objectsReset,
  getUsers,
  getApiKeys,
  getEntityAccessRightsList,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const Container = connector(DataExplorer);

type PropsFromRedux = ConnectedProps<typeof connector>;

export { Container as DataExplorer };
